import {UseDialogStateReturn} from '@/hooks/useDialogState';
import {FormProvider} from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/shadcn_components/ui/dialog';
import {SaveButton} from '@/components/refinedev/action_buttons';
import {ReactNode, useCallback, useEffect} from 'react';
import Loader from '@/components/Loader';
import Spinner from '@/components/basic/Spinner';
import {useEditForm, UseEditFormProps} from '@/hooks/refinedev/useEditForm';

/**
 * EditDialog: A dialog for editing a resource
 */

export type EditDialogProps = {
  title?: ReactNode;
  children?: ReactNode;
  dialogState: UseDialogStateReturn;
  onSubmit?: (data: any) => Promise<void>;
  closeWhenSubmit?: boolean;
} & Partial<UseEditFormProps>;

export const EditDialog = (props: EditDialogProps) => {
  const {
    // edit form props
    resource,
    id,
    formProps,
    transformBeforeSave,
    submitDirtyFieldsOnly,
    // dialog props
    dialogState,
    title,
    children,
    closeWhenSubmit = true,
  } = props;

  const form = useEditForm({
    resource,
    id,
    formProps: {
      ...formProps,
      refineCoreProps: {
        ...formProps?.refineCoreProps,
        queryOptions: {
          ...formProps?.refineCoreProps?.queryOptions,
          enabled: !!id,
        },
      },
    },
    transformBeforeSave,
    submitDirtyFieldsOnly,
  });

  const {
    handleSubmit,
    refineCore: {onFinish, query},
  } = form;

  const {onSubmit} = props; // default submit is onFinish of refineCore

  const onSubmitDefault = useCallback(
    async submittedData => {
      if (onSubmit) await onSubmit?.(submittedData);
      else await onFinish(submittedData);

      if (closeWhenSubmit) {
        // form.reset();
        dialogState.closeDialog();
      }
    },
    [onSubmit, dialogState, closeWhenSubmit, onFinish],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      console.log('dialog open', open);
      if (!open) {
        // form.reset();
      }
      dialogState.setOpen(open);
    },
    [dialogState],
  );

  // reset form when query data changes
  useEffect(() => {
    if (query?.data) {
      form.reset(query.data.data);
    }
  }, [query?.data]);

  return (
    <Dialog open={dialogState.open} onOpenChange={onOpenChange}>
      <DialogContent>
        {query?.isLoading ? (
          <Spinner />
        ) : (
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmitDefault)}>
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription></DialogDescription>
              </DialogHeader>
              <div className="scroll max-h-[calc(100vh-150px)]">{children}</div>
              <DialogFooter className="justify-end mt-4">
                <SaveButton type="button" onClick={handleSubmit(onSubmitDefault)} />
              </DialogFooter>
            </form>
          </FormProvider>
        )}
      </DialogContent>
    </Dialog>
  );
};
